import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import moment from 'moment';

import { FHSimpleTable } from '../../../base';
import { history } from '../../../../helpers';
import { claimHistoryDataTransformer } from '../utils';
import { claimHistoryColumns, getHistoryTableOptions } from '../table-configs/';

import type { HistoricalDocuments } from '../../../../pages/constants';
import type { IClaim, IServiceCode, ISessionWithClaimStatus } from '../../../../utils/types';

interface IClaimHistoryTableData {
  id?: number;
  claimed_date: string;
  client: string;
  created_by: string;
  bcba_name: string;
  no_of_sessions: number;
  claim_id: string;
  status: string;
  recoupment_probability: string;
  recoupment_reason: string;
  sessions: ISessionWithClaimStatus[];
  service_code: IServiceCode;
  clearing_house_claim_id: string;
}

interface IBillingHistoryTableProps {
  data: IClaim[];
  documentType: HistoricalDocuments;
}

function ClaimHistoryTable({ data = [], documentType }: IBillingHistoryTableProps) {
  const tableOptions = useMemo(() => {
    return {
      ...getHistoryTableOptions(documentType),
      onRowClick: (args: any) => {
        history.navigate(`/billings/claims/${args[0]}`);
      },
    };
  }, []);
  const tableColumns = useMemo(
    () => [
      ...claimHistoryColumns,
      {
        name: 'no_of_sessions',
        label: 'No. of sessions',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value: string, _tableMeta: any) => {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  position: 'relative',
                }}
              >
                <Typography sx={{ mt: 1.25 }}>{value}</Typography>
              </Box>
            );
          },
        },
      },
      {
        name: 'bay',
        label: 'Bay',
        options: {
          filter: false,
          sort: false,
        },
      },
    ],
    [],
  );

  const [historyTableData, setHistoryTableData] = useState<IClaimHistoryTableData[]>([]);

  useEffect(() => {
    const transformedTableData = claimHistoryDataTransformer(data);
    transformedTableData.sort((a: IClaimHistoryTableData, b: IClaimHistoryTableData) => {
      if (moment(a.claimed_date).isBefore(moment(b.claimed_date))) {
        return 1;
      } else if (moment(a.claimed_date).isAfter(moment(b.claimed_date))) {
        return -1;
      }
      return 0;
    });
    setHistoryTableData(transformedTableData);
  }, [data]);

  return (
    <Box data-testid="historicalClaimsTable">
      <FHSimpleTable
        data={historyTableData}
        columnDefinitions={tableColumns}
        columnOptions={tableOptions}
      />
    </Box>
  );
}

export default ClaimHistoryTable;
